// Cliente HTTP con token y refresh automático (documento 11). El refresh se hace una sola vez aunque fallen varias
// peticiones a la vez, y rota: el nuevo refresh reemplaza al anterior.
import { API_URL } from '../config';
import { db } from './db';

export class ErrorApi extends Error {
  constructor(
    readonly estado: number,
    readonly codigo: string,
    mensaje: string,
    readonly detalles?: unknown,
  ) {
    super(mensaje);
  }
}

/** Sin conexión o el servidor no respondió: la operación queda para después. */
export class SinRed extends Error {
  constructor() {
    super('Sin conexión');
  }
}

let refrescando: Promise<boolean> | null = null;
type OyenteSesion = (motivo: 'vencida' | 'revocada') => void;
const oyentes = new Set<OyenteSesion>();
export const alPerderSesion = (f: OyenteSesion) => {
  oyentes.add(f);
  return () => {
    oyentes.delete(f);
  };
};

async function refrescar(): Promise<boolean> {
  const s = await db.sesion.get('actual');
  if (!s) return false;
  try {
    const r = await fetch(`${API_URL}/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: s.refresh_token }),
    });
    if (!r.ok) {
      const cuerpo = (await r.json().catch(() => ({}))) as { error?: { codigo?: string } };
      oyentes.forEach((f) => f(cuerpo.error?.codigo === 'TOKEN_REVOCADO' ? 'revocada' : 'vencida'));
      return false;
    }
    const t = (await r.json()) as { access_token: string; refresh_token: string };
    await db.sesion.update('actual', { access_token: t.access_token, refresh_token: t.refresh_token });
    return true;
  } catch {
    throw new SinRed();
  }
}

export interface Opciones {
  metodo?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  cuerpo?: unknown;
  sinToken?: boolean;
  timeoutMs?: number;
}

export async function api<T>(ruta: string, o: Opciones = {}): Promise<T> {
  const hacer = async () => {
    const s = o.sinToken ? null : await db.sesion.get('actual');
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), o.timeoutMs ?? 15_000);
    try {
      return await fetch(`${API_URL}/v1${ruta}`, {
        method: o.metodo ?? (o.cuerpo === undefined ? 'GET' : 'POST'),
        headers: {
          ...(o.cuerpo !== undefined && { 'Content-Type': 'application/json' }),
          ...(s && { Authorization: `Bearer ${s.access_token}` }),
        },
        body: o.cuerpo === undefined ? undefined : JSON.stringify(o.cuerpo),
        signal: ctrl.signal,
      });
    } catch {
      throw new SinRed();
    } finally {
      clearTimeout(t);
    }
  };
  let r = await hacer();
  if (r.status === 401 && !o.sinToken) {
    refrescando ??= refrescar().finally(() => {
      refrescando = null;
    });
    if (await refrescando) r = await hacer();
  }
  if (r.status === 204) return undefined as T;
  const cuerpo = (await r.json().catch(() => null)) as {
    error?: { codigo: string; mensaje: string; detalles?: unknown };
  } | null;
  if (!r.ok) {
    throw new ErrorApi(
      r.status,
      cuerpo?.error?.codigo ?? 'ERROR',
      cuerpo?.error?.mensaje ?? `Error ${r.status}`,
      cuerpo?.error?.detalles,
    );
  }
  return cuerpo as T;
}

export const enLinea = (): boolean => typeof navigator === 'undefined' || navigator.onLine !== false;
